import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Calculator, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, apiError } from "@/lib/api";
import { toDateStr } from "@/lib/slots";

export default function DailyClosing() {
  const today = toDateStr(new Date());
  const [date, setDate] = useState(today);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const gen = async () => {
    setBusy(true);
    try { const r = await api.get("/admin/closing", { params: { date } }); setData(r.data); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const printReport = () => {
    if (!data) return;
    const m = data.methods;
    const html = `<html><head><title>Closing ${data.date}</title><style>
      @page{size:80mm auto;margin:0}
      *{font-family:'Courier New',monospace}
      body{width:72mm;margin:0 auto;padding:6px 4px;color:#000;font-size:12px;line-height:1.5}
      .c{text-align:center}.b{font-weight:bold}.big{font-size:15px}
      .r{display:flex;justify-content:space-between}
      hr{border:none;border-top:1px dashed #000;margin:5px 0}
    </style></head><body>
      <div class="c b big">PLAY STATION</div>
      <div class="c b">DAILY CLOSING REPORT</div>
      <div class="c">${data.date}</div>
      <hr/>
      <div class="r"><span>Bills</span><span>${data.bills_count}</span></div>
      <div class="r"><span>Bookings</span><span>${data.bookings_count}</span></div>
      <div class="r"><span>Food Orders</span><span>${data.orders_count}</span></div>
      <hr/>
      <div class="r"><span>Gaming</span><span>Rs ${data.gaming_total}</span></div>
      <div class="r"><span>Food & Drinks</span><span>Rs ${data.food_total}</span></div>
      ${data.discount > 0 ? `<div class="r"><span>Discounts</span><span>-Rs ${data.discount}</span></div>` : ""}
      <div class="r b big"><span>GROSS TOTAL</span><span>Rs ${data.gross}</span></div>
      <hr/>
      <div class="b">COLLECTIONS</div>
      <div class="r"><span>Cash (${m.cash.count})</span><span>Rs ${m.cash.amount}</span></div>
      <div class="r"><span>UPI (${m.upi.count})</span><span>Rs ${m.upi.amount}</span></div>
      <div class="r"><span>PayU/Online (${m.payu.count})</span><span>Rs ${m.payu.amount}</span></div>
      <hr/>
      <div class="r"><span>Cash Received</span><span>Rs ${data.cash_received}</span></div>
      <div class="r"><span>Change Returned</span><span>Rs ${data.change_returned}</span></div>
      <div class="r b"><span>CASH IN DRAWER</span><span>Rs ${data.net_cash}</span></div>
      <hr/>
      <div class="c b">END OF DAY</div>
      <div class="c">GOOD GAMES BETTER PEOPLE</div>
    </body></html>`;
    const w = window.open("", "_blank", "width=380,height=640");
    if (!w) { toast.error("Allow pop-ups to print the report"); return; }
    w.document.write(html); w.document.close(); w.focus();
    setTimeout(() => { w.print(); }, 400);
  };

  const Stat = ({ label, value, c = "" }) => (
    <div className="rounded-2xl bg-purple-950/30 border border-purple-500/20 p-4" data-testid={`closing-stat-${label}`}>
      <p className={`text-2xl font-bold ${c}`}>₹{value}</p>
      <p className="text-xs text-slate-400 uppercase tracking-wider mt-1">{label}</p>
    </div>
  );
  const MethodCard = ({ label, m, c }) => (
    <div className={`rounded-2xl border p-4 ${c}`} data-testid={`closing-method-${label}`}>
      <p className="text-xs uppercase tracking-wider text-slate-300">{label}</p>
      <p className="text-2xl font-bold mt-1">₹{m.amount}</p>
      <p className="text-xs text-slate-400 mt-0.5">{m.count} bill{m.count === 1 ? "" : "s"}</p>
    </div>
  );

  return (
    <div className="glass rounded-2xl p-5 mb-5" data-testid="closing-report">
      <div className="flex items-center gap-2 mb-1"><Calculator className="text-purple-300" size={22} /><h3 className="font-display font-bold">Daily Closing Report</h3></div>
      <p className="text-sm text-slate-400 mb-4">One-tap end-of-day till reconciliation — cash, UPI, PayU & food totals.</p>
      <div className="flex flex-wrap gap-3 items-end mb-4">
        <div><label className="text-xs text-purple-400 font-mono">Date</label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="closing-date" className="mt-1 h-10 bg-purple-950/30 border-purple-500/30 [color-scheme:dark]" /></div>
        <Button onClick={gen} data-testid="closing-generate" className="h-10 bg-purple-600 hover:bg-purple-500">{busy ? <Loader2 className="animate-spin" size={16} /> : "Generate"}</Button>
        {data && <Button onClick={printReport} data-testid="closing-print" className="h-10 bg-emerald-600 hover:bg-emerald-500"><Printer size={16} /> Print</Button>}
      </div>

      {data && (
        <div className="space-y-4" data-testid="closing-summary">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Gross" value={data.gross} c="text-emerald-400" />
            <Stat label="Gaming" value={data.gaming_total} />
            <Stat label="Food" value={data.food_total} />
            <Stat label="Discounts" value={data.discount} c="text-amber-400" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <MethodCard label="Cash" m={data.methods.cash} c="border-emerald-500/30 bg-emerald-500/5" />
            <MethodCard label="UPI" m={data.methods.upi} c="border-sky-500/30 bg-sky-500/5" />
            <MethodCard label="PayU / Online" m={data.methods.payu} c="border-purple-500/30 bg-purple-500/5" />
          </div>
          <div className="rounded-2xl bg-purple-950/30 border border-purple-500/20 p-4 flex items-center justify-between flex-wrap gap-2">
            <span className="text-sm text-slate-400">Cash Received ₹{data.cash_received} · Change Returned ₹{data.change_returned} · {data.bills_count} bill(s), {data.orders_count} order(s)</span>
            <span className="font-display text-2xl font-bold text-emerald-400" data-testid="closing-net-cash">Cash in Drawer ₹{data.net_cash}</span>
          </div>
        </div>
      )}
    </div>
  );
}
